import React from 'react'
import { Grid } from '@mui/material'
import { linearProgressClasses } from '@mui/material/LinearProgress'
import { Circle } from '@mui/icons-material'
import { compact, isNil, sortBy, uniq } from 'lodash'
import moment from 'moment'
import { getColorAdv, getColorDueDate } from 'common/utils/ColorUtil'

// Lexique + couleur des statuts d'un lot client (valeur en base).
export const LOT_STATUS = [
    { value: 1, label: 'Brouillon', color: '#ffc000' },
    { value: 2, label: 'A faire', color: '#92d050' },
    { value: 3, label: 'En cours', color: '#00b050' },
    { value: 4, label: 'Livré', color: '#00b0f0' },
    { value: 5, label: 'Facturable', color: '#ffff00' },
    { value: 6, label: 'Facturé', color: '#2e75b6' },
    { value: 7, label: 'Annulé', color: '#c0392b' },
    { value: 8, label: 'Reporté', color: '#7f8c8d' },
]

export const STATUT_BROUILLON = 1
export const STATUT_FACTURABLE = 5
export const STATUT_FACTURE = 6
export const STATUT_ANNULE = 7
export const STATUT_REPORTE = 8

// Les idées (brouillons, annulés, reportés) ont leur propre menu, distinct des lots.
export const IDEA_STATUSES = [STATUT_BROUILLON, STATUT_ANNULE, STATUT_REPORTE]
export const LOTS_STATUSES = LOT_STATUS.map(s => s.value).filter(v => !IDEA_STATUSES.includes(v))

// Lots pas encore livrés : seuls concernés par l'alerte d'échéance.
const ONGOING_STATUSES = [2, 3]

// Couleur de l'échéance : rouge si dépassée, orange à moins de 7 jours, rien sinon (ou lot livré / facturé...).
export const dueDateColor = (lot) => (
    !isNil(lot.echeance) && ONGOING_STATUSES.includes(lot.statut) ? (getColorDueDate(lot.echeance, null) ?? undefined) : undefined
)

export const statusDef = (statut) => LOT_STATUS.find(s => s.value === statut) ?? LOT_STATUS[0]
export const statusLabel = (statut) => statusDef(statut).label
export const statusColor = (statut) => statusDef(statut).color

// Luminance pour choisir un texte/encre lisible sur une couleur de statut.
const hexLum = (hex) => {
    const h = hex.replace('#', '')
    return 0.299 * parseInt(h.slice(0, 2), 16) + 0.587 * parseInt(h.slice(2, 4), 16) + 0.114 * parseInt(h.slice(4, 6), 16)
}
export const textOn = (c) => (hexLum(c) > 160 ? '#1c2340' : '#fff')
export const readableInk = (c) => (hexLum(c) > 170 ? '#1c2340' : c)

// Statut proposé par défaut pour une nouvelle note : on avance d'un cran dans le flux linéaire
// (Brouillon..Facturé = 1..6). Les statuts terminaux (Facturé/Annulé/Reporté) restent inchangés.
export const nextStatut = (s) => (s >= 6 ? s : s + 1)

// Items du Select statut : pastille dans le label (centrée).
export const LOT_STATUS_ITEMS = LOT_STATUS.map(s => ({
    value: s.value,
    label: <Grid container spacing={1} alignItems='center'><Circle sx={{ color: s.color, fontSize: '1rem' }} />{s.label}</Grid>,
}))

export const progressBarSx = (value) => ({
    height: '8px',
    borderRadius: '4px',
    [`&.${linearProgressClasses.colorPrimary}`]: { backgroundColor: '#e4e9f0' },
    [`& .${linearProgressClasses.bar}`]: { backgroundColor: getColorAdv(value) },
})

export const ticketsLabel = (lot) => {
    const nb = (lot.tickets ?? []).length
    return nb > 0 && `${nb} ticket${nb > 1 ? 's' : ''}`
}

// Chef de projet interne du client : contributeur CHIEF ayant un login Aquasys.
export const getClientChiefId = (customer) => customer?.contributors?.find(c => c.typeContributor === 'CHIEF' && c.login)?.idContact

export const getCustomerItems = (customers) => sortBy(customers.map(c => ({ id: c.id, label: c.name?.value ?? c.name })), 'label')

// Dates « couvertes » par un lot = dates de ses notes d'avancement + son échéance.
const lotDates = (lot) => compact([...(lot.notes ?? []).map(n => n.date), lot.echeance]).map(d => moment(d))
export const lotYears = (lot) => uniq(lotDates(lot).map(d => d.year()))

// Un lot passe le filtre période si l'une de ses dates tombe dans une des années ET un des trimestres
// sélectionnés (critère vide = pas de restriction) : « 2026 + T1 » ne retient que le T1 2026.
export const matchPeriod = (lot, years, quarters) => lotDates(lot).some(d =>
    (!years.length || years.includes(d.year())) && (!quarters.length || quarters.includes(d.quarter())))

// Filtre par défaut (1re visite + réinitialisation) : année courante pré-sélectionnée.
export const defaultLotsFilter = () => ({ years: [new Date().getFullYear()] })
